import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLandingContext } from '@/context/LandingContext';
import { Loader2, ArrowRight, Share, Activity, Gift } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

const CustomLandingPage: React.FC = () => {
  const { settings, isLoading, businessName } = useLandingContext();
  const navigate = useNavigate();

  // Dynamic icon component using a safer approach
  const DynamicIcon = ({ name }: { name?: string }) => {
    if (!name) return null;
    
    // Convert first letter to uppercase for proper icon name format
    const formattedName = name.charAt(0).toUpperCase() + name.slice(1);
    
    // Handle common icons directly to avoid type issues
    switch (formattedName) {
      case 'Share':
        return <Share className="h-6 w-6 mb-2" />;
      case 'Activity':
        return <Activity className="h-6 w-6 mb-2" />;
      case 'Gift':
        return <Gift className="h-6 w-6 mb-2" />;
      default:
        // For other icons, use a safer approach with type casting
        // First cast to unknown, then to the desired type
        const iconModule = LucideIcons as unknown;
        const icons = iconModule as Record<string, React.ComponentType<{ className?: string }>>;
        const IconComponent = icons[formattedName];
        
        return IconComponent ? <IconComponent className="h-6 w-6 mb-2" /> : null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-blue" />
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  // Create a style object with the custom colors
  const styleVars = {
    '--primary-color': settings.primaryColor,
    '--secondary-color': settings.secondaryColor,
    '--accent-color': settings.accentColor,
    '--background-color': settings.backgroundColor,
    '--text-color': settings.textColor
  } as React.CSSProperties;

  return (
    <div style={styleVars} className="min-h-screen flex flex-col">
      {/* Hero section */}
      <section 
        className="py-20 px-4 flex flex-col items-center text-center" 
        style={{ 
          background: `linear-gradient(135deg, ${settings.primaryColor}, ${settings.secondaryColor})`,
          color: 'white'
        }}
      >
        <h1 className="text-4xl md:text-5xl font-bold mb-4 max-w-4xl">{settings.heroTitle}</h1>
        <p className="text-xl max-w-2xl mb-8">{settings.heroSubtitle}</p>
        <Button 
          className="px-8 py-6 text-lg rounded-full shadow-lg transition transform hover:scale-105"
          style={{ 
            backgroundColor: settings.accentColor,
            color: 'white' 
          }}
          onClick={() => navigate('/signup')}
        >
          {settings.ctaButtonText} <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </section>

      {/* Features section */}
      <section className="py-16 px-4" style={{ backgroundColor: settings.backgroundColor }}>
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-bold text-center mb-12"
            style={{ color: settings.textColor }}
          >
            {settings.featureTitle}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {settings.features.map((feature) => (
              <div 
                key={feature.id} 
                className="bg-white p-6 rounded-lg shadow-md text-center"
                style={{ borderTop: `4px solid ${settings.accentColor}` }}
              >
                <div className="flex justify-center">
                  <DynamicIcon name={feature.iconName} />
                </div>
                <h3 
                  className="text-xl font-semibold mb-3"
                  style={{ color: settings.primaryColor }}
                >
                  {feature.title}
                </h3>
                <p style={{ color: settings.textColor }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section 
        className="py-16 px-4 text-center"
        style={{ backgroundColor: settings.secondaryColor, color: 'white' }}
      >
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">{settings.ctaTitle}</h2>
          <p className="text-xl mb-8">{settings.ctaText}</p>
          <Button 
            className="px-8 py-6 text-lg rounded-full shadow-lg transition transform hover:scale-105"
            style={{ 
              backgroundColor: settings.accentColor,
              color: 'white' 
            }}
            onClick={() => navigate('/signup')}
          >
            {settings.ctaButtonText} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Custom footer - without referme.site */}
      <footer 
        className="py-8 px-4 text-center"
        style={{ 
          backgroundColor: settings.primaryColor,
          color: 'white' 
        }}
      >
        <div className="max-w-6xl mx-auto">
          <p>{settings.footerText}</p>
          <div className="mt-4 flex justify-center space-x-4">
            <a href="/terms" className="hover:underline">Terms of Service</a>
            <a href="/privacy" className="hover:underline">Privacy Policy</a>
            <a href="/contact" className="hover:underline">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CustomLandingPage;
